import { useEffect, useState } from 'react';
import { FiUser } from 'react-icons/fi';
import { MdCheckCircle } from 'react-icons/md';
import FormInput from '../../components/forms/input';
import { useToast } from '../../components/toast';

export interface ProfileCardProfile {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    role?: string | null;
}

export interface ProfileCardProps {
    profile: ProfileCardProfile | null;
    /** Called when the user saves edits. Throw to signal failure — the card keeps edit mode open. */
    onSave?: (updates: { name: string; phone: string }) => Promise<void>;
    /** Override the default "Staff Account" caption beneath the role. */
    accountLabel?: string;
}

export default function ProfileCard({ profile, onSave, accountLabel = 'Staff Account' }: ProfileCardProps) {
    const toast = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(profile?.name || '');
    const [phone, setPhone] = useState(profile?.phone || '');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (profile) {
            setName(profile.name || '');
            setPhone(profile.phone || '');
        }
    }, [profile]);

    const handleEdit = async () => {
        if (!onSave) return;
        if (isEditing) {
            setLoading(true);
            try {
                await onSave({ name, phone });
                toast.success('Profile updated');
                setIsEditing(false);
            } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Failed to update profile');
            } finally {
                setLoading(false);
            }
        } else {
            setIsEditing(true);
        }
    };

    const details = [
        { label: 'Full Name', value: profile?.name || '---', field: 'name' as const, current: name, setter: setName },
        { label: 'Email', value: profile?.email || '---', readonly: true },
        { label: 'Contact', value: profile?.phone || '---', field: 'phone' as const, current: phone, setter: setPhone },
        { label: 'Role', value: profile?.role || '---', readonly: true },
    ];

    return (
        <div className="rounded-xl border border-[#eee] bg-white p-6">
            <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-primary text-primary">
                    <FiUser size={28} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-secondary">{profile?.name || 'Loading...'}</h3>
                        {profile && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-[12px] font-medium text-green-700">
                                <MdCheckCircle size={14} className="text-green-600" />
                                Verified
                            </span>
                        )}
                    </div>
                    <p className="text-[14px] text-[#888]">{profile?.role || '---'}</p>
                    <p className="mt-0.5 text-[13px] text-[#aaa]">{accountLabel}</p>
                </div>
            </div>

            <div className="mt-6 space-y-0 divide-y divide-background">
                {details.map((d) => (
                    <div key={d.label} className="flex items-center justify-between gap-3 py-2.5">
                        <span className="text-[14px] text-[#888]">{d.label}</span>
                        {isEditing && !d.readonly ? (
                            <FormInput
                                type="text"
                                value={d.current}
                                onChange={(e) => d.setter?.(e.target.value)}
                                variant="white"
                                containerClassName="w-1/2"
                                className="py-2! text-right! text-[14px]!"
                            />
                        ) : (
                            <span className="text-[14px] font-medium text-secondary">{d.value}</span>
                        )}
                    </div>
                ))}
            </div>

            {onSave && (
                <div className="mt-4 flex justify-center">
                    <button
                        type="button"
                        onClick={handleEdit}
                        disabled={loading || !profile}
                        className="rounded-lg bg-secondary px-8 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-secondary-400 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : isEditing ? 'Save Details' : 'Edit Details'}
                    </button>
                </div>
            )}
        </div>
    );
}
